import { Injectable } from '@angular/core';
import { select, Store, Action } from '@ngrx/store';
import * as ConsultationActions from './consultation.actions';
import * as ConsultationSelectors from './consultation.selectors';
import * as ConsultationReducer from './consultation.reducer';
import {
  Answer,
  Consultation,
  IRequiredFormsParam,
  IFile
} from '@care-portals/api-interfaces';
import { FormEngineFacade } from '../../common-components/form-engine/form-engine.component';
import { FilesFacade } from '../../common-components/question/question-image-list/question-image-list.component';
import { APIService } from '../../services/api/api-service';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class ConsultationFacade implements FormEngineFacade, FilesFacade {
  loading$ = this.store.pipe(
    select(ConsultationSelectors.getConsultationLoading)
  );
  uploading$ = this.store.pipe(select(ConsultationSelectors.getImageUploading));
  saving$ = this.store.pipe(
    select(ConsultationSelectors.getConsultationSaving)
  );
  busy$ = this.store.pipe(select(ConsultationSelectors.getConsultationBusy));
  answers$ = this.store.pipe(select(ConsultationSelectors.getAnswersState));

  selectedFormType$ = this.store.pipe(
    select(ConsultationSelectors.getSelectedFormType)
  );
  selectedQuestionType$ = this.store.pipe(
    select(ConsultationSelectors.getSelectedQuestionType)
  );

  selectedAnswer$ = this.store.pipe(
    select(ConsultationSelectors.getSelectedAnswer)
  );
  files$ = this.store.pipe(select(ConsultationSelectors.getFiles));
  changedFiles$ = this.store.pipe(
    select(ConsultationSelectors.getChangedFiles)
  );

  formsState$ = this.store.pipe(select(ConsultationSelectors.getFormsState));
  questionsState$ = this.store.pipe(
    select(ConsultationSelectors.getQuestionsState)
  );
  isLastQuestion$ = this.store.pipe(
    select(ConsultationSelectors.isLastQuestion)
  );
  isFirstQuestion$ = this.store.pipe(
    select(ConsultationSelectors.isFirstQuestion)
  );

  selectedQuestionModel$ = this.store.pipe(
    select(ConsultationSelectors.getSelectedQuestionModel)
  );
  formCompleted$ = this.store.pipe(
    select(ConsultationSelectors.getFormCompleted)
  );
  draftConsult$ = this.store.pipe(
    select(ConsultationSelectors.getDraftConsultation)
  );

  uploading: boolean;
  waiting = false;

  constructor(
    private store: Store<ConsultationReducer.State>,
    private readonly api: APIService
  ) {
    this.uploading$.subscribe((res: boolean) => {
      this.uploading = res;
      if (this.waiting && !this.uploading) {
        this.waiting = false;
        this.saveForm();
      }
    });
  }

  dispatch(action: Action) {
    this.store.dispatch(action);
  }

  getRequiredForms(query: IRequiredFormsParam = {}) {
    this.dispatch(ConsultationActions.getRequiredForms({ query }));
  }

  storeAnswer(selectedAnswer, states = {}) {
    this.store.dispatch(
      ConsultationActions.storeAnswer({ selectedAnswer, states })
    );
  }

  nextQuestion() {
    this.store.dispatch(ConsultationActions.nextQuestion());
  }

  prevQuestion() {
    this.store.dispatch(ConsultationActions.prevQuestion());
  }

  setDraftConsult(draftConsult: Consultation) {
    this.store.dispatch(ConsultationActions.setDraftConsult({ draftConsult }));
  }

  setAnswer(answer: Answer) {
    this.store.dispatch(ConsultationActions.setAnswer({ answer }));
  }

  setRawFile(resourceId: string, fileItem: IFile) {
    this.store.dispatch(
      ConsultationActions.setRawFile({ resourceId, fileItem })
    );
  }

  getFile(name: string, resourceId: string) {
    this.dispatch(ConsultationActions.getFile({ name, resourceId }));
  }

  saveForm() {
    if (this.uploading) {
      this.waiting = true;
    } else {
      this.store.dispatch(ConsultationActions.saveForm());
    }
  }

  deleteDraftConsult(formType: string) {
    this.dispatch(ConsultationActions.deleteDraftConsult({ formType }));
  }

  async uploadFile(file: IFile) {
    try {
      return firstValueFrom(await this.api.uploadFile(file));
    } catch (error) {
      console.error(error);
      return error;
    }
  }
}
